import React, { useRef } from 'react'
import { StyleSheet, View } from 'react-native'

import styles, { colors } from '../../../../styles/global'
import SocialText from '../../../../UIControls/SocialText/SocialText'
import { isGmailLabelFollowUpTask } from '../../../../../utils/Gmail/gmailTaskUtils'
import GmailTag from '../../../../Tags/GmailTag'
import TaskCompletionProgress from './TaskCompletionProgress'

export default function TitleContainer({
    task,
    projectId,
    isObservedTask,
    toggleModal,
    backColorHighlight,
    backColor,
    hasStar,
    inMyDayAndNotSubtask,
    blockOpen,
    tagsExpandedHeight,
    showVerticalEllipsisInByTime,
    leadingVmStatusTag,
    leadingPriorityTag,
    leadingReminderTag,
    useCommentPopupTextColor,
    setTaskTitleIsMultiline,
    completionProgress,
}) {
    const gmailTag = isGmailLabelFollowUpTask(task) ? (
        <GmailTag gmailData={task.gmailData} propStyles={localStyles.gmailTag} showLabel iconSize={12} />
    ) : null

    // In the by-time view the priority chip is rendered in the left time area
    // (TaskTagsContainerByTime); here it leads the title so it sits next to the calendar tag.
    const leftCustomElement = (
        <>
            {!inMyDayAndNotSubtask && leadingReminderTag}
            {!inMyDayAndNotSubtask && leadingPriorityTag}
            {gmailTag}
        </>
    )

    // AT-2404 — kept in a ref rather than state: the progress overlay only mounts on a completion,
    // which is itself a re-render, so the latest measurement is always available by then. Holding
    // it in state instead would re-render every title on every reflow to serve a 700ms effect.
    const titleHeightRef = useRef(0)

    const onTaskTitleLayout = ({ nativeEvent: { layout } }) => {
        const lineHeight = task.isSubtask ? styles.body2.lineHeight : styles.body1.lineHeight
        const wrappedTextVerticalMargins = 6
        titleHeightRef.current = layout.height
        setTaskTitleIsMultiline(layout.height > lineHeight + wrappedTextVerticalMargins)
    }

    return (
        <View
            style={[
                localStyles.descriptionContainer,
                task.isSubtask ? localStyles.descriptionContainerSubtask : undefined,
            ]}
        >
            <SocialText
                elementId={`social_text_${projectId}_${task.id}_${isObservedTask}`}
                onTextLayout={onTaskTitleLayout}
                style={[
                    task.isSubtask ? styles.body2 : styles.body1,
                    localStyles.descriptionText,
                    task.isSubtask ? localStyles.descriptionTextSubtask : undefined,
                    task.isSubtask && task.done ? { color: colors.Text03 } : undefined,
                    useCommentPopupTextColor ? { color: colors.UtilityBlue100 } : undefined,
                ]}
                onPress={toggleModal}
                numberOfLines={3}
                wrapText={true}
                hasLinkBack={task.linkBack !== undefined && task.linkBack.length > 0}
                task={task}
                hasStar={hasStar}
                isSubtask={task.isSubtask}
                bgColor={hasStar ? backColorHighlight : backColor}
                projectId={projectId}
                blockOpen={blockOpen}
                leadingStatusElement={!inMyDayAndNotSubtask ? leadingVmStatusTag : null}
                leftCustomElement={leftCustomElement}
                activeCalendarStyle={inMyDayAndNotSubtask}
                tagsExpandedHeight={tagsExpandedHeight}
                isObservedTask={isObservedTask}
                showVerticalEllipsisInByTime={showVerticalEllipsisInByTime}
                dotsStyle={inMyDayAndNotSubtask && { paddingLeft: 6, bottom: 12 }}
                allowPressInsidePopover={useCommentPopupTextColor}
            >
                {task !== undefined && task.name != null && task.extendedName != null
                    ? task.extendedName !== ''
                        ? task.extendedName
                        : task.name
                    : ''}
            </SocialText>
            {completionProgress && (
                <TaskCompletionProgress
                    progress={completionProgress.progress}
                    pulse={completionProgress.pulse}
                    opacity={completionProgress.opacity}
                    measuredHeight={titleHeightRef.current}
                    isSubtask={task.isSubtask}
                    // Same DOM id `TasksHelper.showWrappedTaskEllipsis` already measures this title
                    // by, so the sweep can size itself to the text instead of to the flex column.
                    elementId={`social_text_${projectId}_${task.id}_${isObservedTask}`}
                />
            )}
        </View>
    )
}

const localStyles = StyleSheet.create({
    descriptionContainer: {
        flexGrow: 1,
        paddingLeft: 12,
        flex: 1,
    },
    descriptionContainerSubtask: {
        paddingLeft: 10,
    },
    descriptionText: {
        display: 'flex',
        marginTop: 5,
        marginBottom: 5,
        alignItems: 'flex-start',
        maxHeight: 90,
    },
    descriptionTextSubtask: {
        marginTop: 6,
        marginBottom: 6,
        maxHeight: 90,
    },
    gmailTag: {
        marginRight: 8,
    },
})
